<script setup lang="ts">
import type { DevicePreviewMode } from '@/composables/useDevicePreview';

const props = defineProps<{ embedded?: boolean }>();

const { mode, options, widthLabel, setMode, load, ready } = useDevicePreview();
const { t } = useI18n();

onMounted(() => {
  // Jangan reload preview tiap panel profil dibuka — cukup sekali saat belum ready
  if (!ready.value) load();
});

function pick(id: DevicePreviewMode) {
  if (mode.value === id) return;
  // setMode sendiri menunda apply agar parent (ProfileDD) tidak unmount di tengah klik
  setMode(id);
}
</script>

<template>
  <div
    class="device-switch"
    :class="{ 'device-switch--embedded': props.embedded }"
    role="group"
    :aria-label="t('device.label')"
  >
    <div class="device-switch__glow" aria-hidden="true" />
    <button
      v-for="opt in options"
      :key="opt.id"
      type="button"
      class="device-btn"
      :class="[
        `device-btn--${opt.id}`,
        { 'device-btn--active': mode === opt.id },
      ]"
      :title="`${opt.title} · ${opt.hint}`"
      :aria-pressed="mode === opt.id"
      @click="pick(opt.id)"
    >
      <span class="device-btn__icon" aria-hidden="true">
        <v-icon size="16">{{ opt.icon }}</v-icon>
      </span>
      <span class="device-btn__text">
        <span class="device-btn__label">{{ opt.title }}</span>
        <span class="device-btn__w" :class="{ 'device-btn__w--on': mode === opt.id }">
          {{ mode === opt.id ? widthLabel : '\u00a0' }}
        </span>
      </span>
    </button>
  </div>
</template>

<style scoped>
.device-switch {
  --ds-ink: #0f172a;
  --ds-muted: #334155;
  --ds-line: #cbd5e1;
  position: relative;
  display: inline-flex;
  align-items: stretch;
  gap: 3px;
  padding: 3px;
  margin-right: 8px;
  border-radius: 14px;
  background: #f1f5f9;
  border: 1px solid var(--ds-line);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}
.device-switch--embedded {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  width: 100%;
  margin-right: 0;
  border-radius: 12px;
}
.device-switch__glow {
  pointer-events: none;
  position: absolute;
  inset: -40% auto auto -20%;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(15, 118, 110, 0.18), transparent 70%);
}
.device-btn {
  position: relative;
  z-index: 1;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: none;
  cursor: pointer;
  padding: 8px 6px;
  border-radius: 11px;
  background: transparent;
  color: var(--ds-muted);
  font-family: inherit;
  transition:
    background 0.18s ease,
    color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.15s ease;
}
.device-switch--embedded .device-btn {
  height: 44px;
  min-height: 44px;
  max-height: 44px;
  padding: 4px 2px;
  gap: 2px;
  align-self: stretch;
  transform: none !important;
}
.device-switch--embedded .device-btn--active {
  transform: none !important;
}
.device-btn__w {
  font-size: 9px;
  font-weight: 700;
  opacity: 0;
  font-variant-numeric: tabular-nums;
  height: 11px;
  line-height: 11px;
  min-height: 11px;
}
.device-btn__w--on {
  opacity: 0.85;
}

/* HP: tinggi tombol seragam (lebih padat di sheet) */
:global(html.jgi-device-hp .device-switch--embedded .device-btn),
:global(.jgi-device-studio--hp .device-switch--embedded .device-btn) {
  height: 38px;
  min-height: 38px;
  max-height: 38px;
  padding: 4px 2px;
  gap: 2px;
  border-radius: 8px;
  transform: none !important;
}
:global(html.jgi-device-hp .device-switch--embedded),
:global(.jgi-device-studio--hp .device-switch--embedded) {
  padding: 3px;
  gap: 3px;
  border-radius: 12px;
}
:global(html.jgi-device-hp .device-btn__icon),
:global(.jgi-device-studio--hp .device-btn__icon) {
  width: 20px;
  height: 20px;
  border-radius: 6px;
}
:global(html.jgi-device-hp .device-btn__icon .v-icon),
:global(.jgi-device-studio--hp .device-btn__icon .v-icon) {
  font-size: 13px !important;
  width: 13px !important;
  height: 13px !important;
}
:global(html.jgi-device-hp .device-btn__label),
:global(.jgi-device-studio--hp .device-btn__label) {
  font-size: 10px;
}
:global(html.jgi-device-hp .device-btn__w),
:global(.jgi-device-studio--hp .device-btn__w) {
  font-size: 8px;
  height: 10px;
  min-height: 10px;
  line-height: 10px;
}
.device-btn__icon {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 7px;
  background: #e2e8f0;
  color: #334155;
  transition: background 0.18s ease, color 0.18s ease;
}
.device-btn__text {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.05;
  text-align: center;
}
.device-btn__label {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: inherit;
}
.device-btn:hover:not(.device-btn--active) {
  color: var(--ds-ink);
  background: rgba(255, 255, 255, 0.65);
}
.device-btn:hover:not(.device-btn--active) .device-btn__icon {
  background: rgba(15, 23, 42, 0.07);
}
.device-btn--active {
  color: #fff;
}
.device-btn--desktop.device-btn--active {
  background: linear-gradient(135deg, #0f766e 0%, #115e59 55%, #134e4a 100%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.2) inset,
    0 8px 16px -10px rgba(15, 118, 110, 0.7);
}
.device-btn--tablet.device-btn--active {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 55%, #1e3a8a 100%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.2) inset,
    0 8px 16px -10px rgba(37, 99, 235, 0.7);
}
.device-btn--hp.device-btn--active {
  background: linear-gradient(135deg, #ea580c 0%, #c2410c 55%, #9a3412 100%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.2) inset,
    0 8px 16px -10px rgba(234, 88, 12, 0.7);
}
.device-btn--active .device-btn__icon {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}

:global(html.jgi-dark .device-switch){
  --ds-ink: #f8fafc;
  --ds-muted: #94a3b8;
  --ds-line: rgba(248, 250, 252, 0.12);
  background: linear-gradient(145deg, rgba(30, 41, 59, 0.95), rgba(15, 23, 42, 0.9));
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.06) inset,
    0 8px 20px -14px rgba(0, 0, 0, 0.55);
}
:global(html.jgi-dark .device-btn__icon){
  background: rgba(248, 250, 252, 0.08);
}
:global(html.jgi-dark .device-btn:hover:not(.device-btn--active)){
  background: rgba(255, 255, 255, 0.06);
}
</style>
